// Escáner full-screen del QR de factura (payload CPE:INV:<invnum>, impreso por
// la app de Rutas). Usa BarcodeDetector nativo cuando existe (Chrome/Android);
// si no, cae a zxing-wasm leyendo frames del canvas cada ~300ms (iOS/Safari).

import { useEffect, useRef, useState } from 'react';

const PAYLOAD_RE = /^CPE:INV:(\S+)$/;

export function parseInvoicePayload(raw: string): string | null {
  const text = raw.trim();
  const m = PAYLOAD_RE.exec(text);
  if (m) return m[1] ?? null;
  if (/^\d{4,12}$/.test(text)) return text;
  return null;
}

type BarcodeDetectorLike = {
  detect: (source: CanvasImageSource) => Promise<{ rawValue: string }[]>;
};
type BarcodeDetectorCtor = new (options: { formats: string[] }) => BarcodeDetectorLike;
type ZxingReadFn = (
  input: ImageData,
  options?: { formats?: string[]; tryHarder?: boolean },
) => Promise<{ text: string }[]>;

type Props = {
  onScan: (invnum: string) => void;
  onClose: () => void;
};

export function InvoiceQrScanner({ onScan, onClose }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const doneRef = useRef(false);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let timer: number | undefined;
    let cancelled = false;
    let busy = false;
    const canvas = document.createElement('canvas');

    const stop = () => {
      if (timer !== undefined) window.clearInterval(timer);
      stream?.getTracks().forEach((t) => t.stop());
    };

    const handleMatch = (invnum: string) => {
      if (doneRef.current) return;
      doneRef.current = true;
      try {
        navigator.vibrate?.(200);
      } catch {
        /* vibración no soportada */
      }
      stop();
      onScan(invnum);
    };

    void (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
      } catch {
        if (!cancelled) {
          setError('No se pudo acceder a la cámara. Revisa el permiso de cámara para esta app e inténtalo de nuevo.');
        }
        return;
      }
      if (cancelled) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }

      const video = videoRef.current;
      if (!video) return;
      video.srcObject = stream;
      try {
        await video.play();
      } catch {
        /* play interrumpido por unmount */
      }

      let detector: BarcodeDetectorLike | null = null;
      const Ctor = (window as unknown as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;
      if (Ctor) {
        try {
          detector = new Ctor({ formats: ['qr_code', 'code_128'] });
        } catch {
          detector = null;
        }
      }

      let zxingRead: ZxingReadFn | null = null;
      if (!detector) {
        try {
          const [mod, wasmUrl] = await Promise.all([
            import('zxing-wasm/reader'),
            import('zxing-wasm/reader/zxing_reader.wasm?url').then((m) => m.default),
          ]);
          // Servir el .wasm desde nuestro propio origen (bundle de Vite), no
          // desde el CDN por defecto — la PWA debe funcionar sin red externa.
          mod.prepareZXingModule({
            overrides: {
              locateFile: (path: string, prefix: string) =>
                path.endsWith('.wasm') ? wasmUrl : prefix + path,
            },
          });
          zxingRead = mod.readBarcodes as unknown as ZxingReadFn;
        } catch {
          if (!cancelled) setError('Este dispositivo no soporta el escaneo de códigos.');
          return;
        }
      }

      timer = window.setInterval(async () => {
        const v = videoRef.current;
        if (!v || v.readyState < 2 || doneRef.current || busy) return;
        busy = true;
        try {
          if (detector) {
            const results = await detector.detect(v);
            for (const r of results) {
              const invnum = parseInvoicePayload(r.rawValue);
              if (invnum) return handleMatch(invnum);
            }
          } else if (zxingRead) {
            canvas.width = v.videoWidth;
            canvas.height = v.videoHeight;
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            if (!ctx) return;
            ctx.drawImage(v, 0, 0);
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const results = await zxingRead(imageData, { formats: ['QRCode', 'Code128'], tryHarder: true });
            for (const r of results) {
              const invnum = parseInvoicePayload(r.text);
              if (invnum) return handleMatch(invnum);
            }
          }
        } catch {
          /* frame fallido — seguimos escaneando */
        } finally {
          busy = false;
        }
      }, 300);
    })();

    return () => {
      cancelled = true;
      stop();
    };
  }, [onScan]);

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      <video ref={videoRef} playsInline muted className="absolute inset-0 w-full h-full object-cover" />

      {/* Marco guía */}
      {!error && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-64 h-64 border-4 border-white/80 rounded-2xl shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]" />
        </div>
      )}

      <div className="relative flex items-start justify-between p-4">
        <p className="text-white text-lg font-semibold drop-shadow">Escanea el QR de la factura</p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar escáner"
          className="w-11 h-11 rounded-full bg-white/20 text-white text-2xl font-bold flex items-center justify-center backdrop-blur"
        >
          ✕
        </button>
      </div>

      {error ? (
        <div className="relative m-auto max-w-sm mx-4 bg-white rounded-2xl p-6 text-center space-y-4">
          <div className="text-5xl">📷</div>
          <p className="text-base text-rose-700 font-medium">{error}</p>
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white text-base font-bold rounded-full"
          >
            Cerrar
          </button>
        </div>
      ) : (
        <p className="relative mt-auto mb-8 text-center text-white/90 text-base px-6 drop-shadow">
          Apunta al código QR impreso en la factura (CPE:INV)
        </p>
      )}
    </div>
  );
}
